/**
 * Document model contract (ENGINE-SPEC §2, §11.2). Built by `document/build.ts` from an IntakeResult.
 * Everything is index based: typed arrays, no per-token objects. All offsets refer to `doc.text`.
 */
import type { IntakeFormat, IntakeObservability, IntakeSpan, SpanKind } from '../ingest/types.ts';

export type Register = 'prose' | 'structural' | 'quoted' | 'code';
export const REGISTER_PROSE = 0;
export const REGISTER_STRUCTURAL = 1;
export const REGISTER_QUOTED = 2;
export const REGISTER_CODE = 3;

/** Token classes (§2.5). */
export const TOK_WORD = 0;
export const TOK_NUM = 1;
export const TOK_PUNCT = 2;
export const TOK_ELLIPSIS = 3;
export const TOK_DASH = 4;

/** Sentence flags. */
export const SENT_STRUCTURAL = 1; // heading / list item / table cell unit
export const SENT_RUNON = 2; // > 120 words, no boundary (R12): excluded from length statistics
export const SENT_NOSPACE = 4; // R10: candidate boundary with no whitespace after it

export interface Tokens {
	count: number;
	start: Uint32Array;
	len: Uint16Array;
	/** Interned id of the FOLDED form (§2.1). */
	id: Uint32Array;
	cls: Uint8Array;
	/** Folded string for an interned id. */
	fold(id: number): string;
	/** Interned id for a folded string, or -1 if never seen. */
	lookup(folded: string): number;
	/** Number of interned ids. */
	vocabSize: number;
}

/**
 * Half-open ranges over token / character indices, one entry per unit. A sentence is never empty of tokens, but
 * `words` (WORD tokens) can be 0 for a number- or punctuation-only unit ("5:44.", "§1."): length statistics skip
 * `words === 0`. Structural and code blocks are one unit each (SENT_STRUCTURAL); `register` says which.
 */
export interface Sentences {
	count: number;
	tokStart: Uint32Array;
	tokEnd: Uint32Array;
	charStart: Uint32Array;
	charEnd: Uint32Array;
	/** Word tokens in the sentence. */
	words: Uint32Array;
	block: Uint32Array;
	para: Uint32Array;
	flags: Uint8Array;
	register: Uint8Array;
}

export interface Paragraphs {
	count: number;
	charStart: Uint32Array;
	charEnd: Uint32Array;
	tokStart: Uint32Array;
	tokEnd: Uint32Array;
	sentStart: Uint32Array;
	sentEnd: Uint32Array;
	/**
	 * Word tokens. Structural blocks (headings, list items, scene breaks, …) are attributed to the paragraph that follows
	 * them (§2.8a); a trailing structural run after the last paragraph joins the last paragraph. The paragraphs
	 * therefore PARTITION the token, sentence and (block) space: `tokStart[p+1] === tokEnd[p]` etc. `ownWords` excludes them.
	 */
	words: Uint32Array;
	/** Index of the paragraph's own (main) block. */
	block: Uint32Array;
	/** Register of the own block. */
	register: Uint8Array;
	// --- additions by document/build.ts (W3a) ---
	/** Word tokens of the own block only (prose paragraphs: the exposure of prose-register channels). */
	ownWords: Uint32Array;
	/** First token / sentence / character of the own block (attached structural blocks come before these). */
	ownTokStart: Uint32Array;
	ownSentStart: Uint32Array;
	ownCharStart: Uint32Array;
	/** Index into doc.scenes / doc.chapters (−1 when none). */
	scene: Int32Array;
	chapter: Int32Array;
}

export interface DocBlock {
	kind: string;
	level: number;
	register: Register;
	charStart: number;
	charEnd: number;
	srcStart: number;
	srcEnd: number;
	/** Flat pairs [textOffset (absolute in doc.text), srcOffset, ...]. */
	map: number[];
	/** Index of the paragraph the block is attributed to (structural blocks: the paragraph that follows them). */
	para: number;
	// --- additions by document/build.ts (W3a) ---
	/** The block is a scene break (thematicBreak or a lone `***`, `* * *`, `#`, `⁂`, `~`, `§`, `o`, `---` line). It has no tokens. */
	sceneBreak: boolean;
	/** Token range [tokStart, tokEnd) and word-token count of the block. */
	tokStart: number;
	tokEnd: number;
	words: number;
}

export interface HeadingInfo {
	block: number;
	level: number;
	text: string;
	charStart: number;
	/** Paragraph the heading is attached to (the one that follows it; a heading after the last paragraph: the last one). */
	para: number;
}

/** A contiguous run of paragraphs [paraStart, paraEnd) that is a natural unit. */
export interface UnitRange {
	paraStart: number;
	paraEnd: number;
	charStart: number;
	charEnd: number;
	/** Prose words. */
	words: number;
	title?: string;
}

export interface DocSpan extends IntakeSpan {
	charStart: number;
	charEnd: number;
}

export interface DocStats {
	words: number;
	wordsByRegister: Record<Register, number>;
	sentences: number;
	proseSentences: number;
	paragraphs: number;
	scenes: number;
	chapters: number;
	dialogueShare: number;
}

export interface DocFlags {
	registerFallback: boolean;
	hardWrapped: boolean;
	veryShort: boolean;
	pdfCleaned: boolean;
	languageUnsupported: boolean;
	truncatedIntake: boolean;
	/** The writer ends sentences without capitals: the splitter's lowercase-writer mode was on (ENGINE-SPEC §2.6, C4). */
	lowerWriter: boolean;
}

/** One normalisation event of the intake hygiene step (ENGINE-SPEC §2.1 N7, N8). */
export interface HygieneEvent {
	/** Offset in the final `doc.text` (for a removed character, the offset where it was). */
	offset: number;
	kind: 'invisible' | 'lookalike';
	/** The removed invisible character, or the Cyrillic letter that was mapped to Latin. */
	codePoint: number;
}

/**
 * The hygiene indicator (ENGINE-SPEC §2.1, T-DOC-07): what N7 and N8 changed. Evidence only, never a channel score or a
 * model input. Rates are per 1,000 characters of the final `doc.text` (0 when the text is empty).
 */
export interface DocHygiene {
	/** N7: invisible characters removed. */
	invisibleRemoved: number;
	/** N8: look-alike letters mapped to Latin. */
	lookalikesMapped: number;
	invisiblePer1k: number;
	lookalikesPer1k: number;
	/** The first 5 events by position. */
	first: HygieneEvent[];
}

export interface Doc {
	text: string;
	format: IntakeFormat;
	language: { code: string; confidence: number };
	blocks: DocBlock[];
	tokens: Tokens;
	sentences: Sentences;
	paragraphs: Paragraphs;
	headings: HeadingInfo[];
	/** Scene boundaries are inside chapters; `sections` = scenes, else chapters, else the whole document (§2.8a). */
	chapters: UnitRange[];
	scenes: UnitRange[];
	sections: UnitRange[];
	spans: DocSpan[];
	stats: DocStats;
	flags: DocFlags;
	observability: IntakeObservability & { quotes: 'curly' | 'straight' | 'mixed' };
	/**
	 * Character offsets of every thematic-break / scene-break line in doc.text, as FLAT PAIRS
	 * `[start0, end0, start1, end1, …]` (half-open; `end === start` when the break has no text).
	 * Scene-break count = `sceneBreaks.length / 2`.
	 */
	sceneBreaks: number[];
	/** Doc-wide word tokens in the prose register (the exposure unit of most channels). */
	proseWords: number;
	/** Source-position mapping helpers. */
	toSource(textOffset: number): number;
	/** Folded text of token i (lazy). */
	tokenText(i: number): string;
	/** doc.text.slice of token i. */
	tokenRaw(i: number): string;
	spansOfKind(kind: SpanKind, paraIndex?: number): DocSpan[];
	/** 32-bit FNV-1a hash of the text (UTF-16 code units, low byte then high byte), used to seed randomness. */
	hash: number;
	// --- additions by document/build.ts (W3a) ---
	/**
	 * Share of prose-register WORD tokens that are function words of the document's pack (English pack when the
	 * language has none). English-native channels abstain below `EN_GATE` (0.25; lang/index.ts): C-LANG.
	 */
	functionWordShare: number;
	/** N7/N8 counts and first positions (always present; zeros for clean text). */
	hygiene: DocHygiene;
}
